import { Injectable, inject } from '@angular/core';
import { DashboardUserService } from '@rma/authentication/public/user';
import { AwardsLeaderboardService } from '@rma/awards/public/api-awards-leaderboard';
import { CurrentSubscriptionService } from '@rma/billing/subscription-plans/public/api-subscription-features';
import { FeatureFlags, FeaturesService } from '@rma/generic/util/feature-flags';
import { rmaShareReplay } from '@rma/generic/util/operators/rma-share-replay';
import { ProfileDetailsContextService } from '@rma/profile/public/api-profile-details';
import { cache } from 'decorator-cache-getter';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PromotionType } from './promotion-type.enum';

type RemaxAd = Extract<PromotionType, 'RemaxGoogle' | 'Remax5Star'>;

@Injectable({
  providedIn: 'root',
})
export class RemaxOverrideService {
  private readonly awardsLeaderboardService = inject(AwardsLeaderboardService);
  private readonly currentSubscriptionService = inject(CurrentSubscriptionService);
  private readonly dashboardUserService = inject(DashboardUserService);
  private readonly featuresService = inject(FeaturesService);
  private readonly profileService = inject(ProfileDetailsContextService);

  @cache
  public get remaxAd$(): Observable<RemaxAd | null> {
    return this.currentSubscriptionService.currentSubscription$.pipe(
      switchMap((x) =>
        x.isFree || !(x.isSubscribed || x.isProvided || x.hasCancelled)
          ? this.isRemax$.pipe(switchMap((isRemax) => (!isRemax ? of(null) : this.remaxAdType)))
          : of(null),
      ),
    );
  }

  private get isRemax$() {
    return combineLatest([
      this.featuresService
        .getFeature$<number[]>(FeatureFlags.DashboardEnableCorporateLeaderboard, [])
        .pipe(map((c): number[] => c.filter((i) => i === 2))),
      this.dashboardUserService.isAgent$,
    ]).pipe(
      switchMap(([corporateIds, isAgent]) => {
        const companyLeaderboardsExist = corporateIds.length > 0;
        const networkInfo$ = this.awardsLeaderboardService
          .getCorporateNetworkInfo()
          .pipe(map((corporateNetworkInfo) => corporateIds.includes(corporateNetworkInfo?.corporateNetworkId)));

        return companyLeaderboardsExist && isAgent ? networkInfo$ : of(false);
      }),
      rmaShareReplay(),
    );
  }

  private get remaxAdType(): Observable<RemaxAd> {
    return this.profileService.profileSummary$.pipe(
      map(({ reviewCount }) => reviewCount),
      map((c) => (c === 0 ? 'Remax5Star' : 'RemaxGoogle')),
    );
  }
}
